import React, { useState, useEffect, useRef } from 'react';
import { Camera, Scan, X, Smartphone, Flashlight, RotateCw } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { BarcodeScanner as CapacitorBarcodeScanner } from '@capacitor-community/barcode-scanner';
import { Capacitor } from '@capacitor/core';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { Html5QrcodeScanner, Html5Qrcode } from 'html5-qrcode';
import { secureLogger } from '@/utils/secureLogger';
interface BarcodeScannerProps {
  onBarcodeScanned: (barcode: string) => void;
  trigger?: React.ReactNode;
  autoStart?: boolean;
  burstMode?: boolean;
  continuousMode?: boolean;
  onScanSuccess?: (barcode: string) => void;
  onScanFailure?: (barcode: string) => void;
  cartTotal?: number;
  cartItemCount?: number;
  showCartBubble?: boolean;
}
export const BarcodeScanner: React.FC<BarcodeScannerProps> = ({ 
  onBarcodeScanned, 
  trigger,
  autoStart = false,
  burstMode = false,
  continuousMode = false,
  onScanSuccess,
  onScanFailure,
  cartTotal = 0,
  cartItemCount = 0,
  showCartBubble = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [manualBarcode, setManualBarcode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNative, setIsNative] = useState(false);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [cameraDevices, setCameraDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [scannerMethod, setScannerMethod] = useState<'html5qrcode' | 'zxing'>('html5qrcode');
  const isMobile = useIsMobile();
  const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null);
  const html5QrcodeRef = useRef<Html5Qrcode | null>(null);
  useEffect(() => {
    setIsNative(Capacitor.isNativePlatform());
    
    // Énumérer les caméras disponibles pour le web
    if (!Capacitor.isNativePlatform()) {
      navigator.mediaDevices?.enumerateDevices()
        .then(devices => {
          const cameras = devices.filter(d => d.kind === 'videoinput');
          setCameraDevices(cameras);
          // Sélectionner la caméra arrière par défaut
          const backCamera = cameras.find(c => c.label.toLowerCase().includes('back') || c.label.toLowerCase().includes('rear'));
          if (backCamera) {
            setSelectedCamera(backCamera.deviceId);
          }
        })
        .catch(console.warn);
    }
  }, []);
  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualBarcode.trim()) {
      onBarcodeScanned(manualBarcode.trim());
      setManualBarcode('');
      setIsOpen(false);
    }
  };
  const startNativeScanning = async () => {
    try {
      setError(null);
      setIsScanning(true);

      // Vérifier les permissions
      const status = await CapacitorBarcodeScanner.checkPermission({
        force: true
      });
      if (!status.granted) {
        throw new Error('Permission caméra refusée');
      }

      // Masquer le background pour voir la caméra
      document.body.classList.add('scanner-active');

      // Activer le flash par défaut
      setTorchEnabled(true);

      // Démarrer le scan avec les paramètres optimisés pour mobile
      const result = await CapacitorBarcodeScanner.startScan({
        targetedFormats: ['EAN_13', 'EAN_8', 'UPC_A', 'UPC_E', 'CODE_128', 'CODE_39', 'CODE_93', 'QR_CODE', 'DATA_MATRIX', 'ITF', 'PDF_417']
      });
      
      if (result.hasContent) {
        // Vibration haptique sur succès
        if (navigator.vibrate) {
          navigator.vibrate([100, 50, 100]);
        }
        onBarcodeScanned(result.content);
        stopScanning(); // Arrêt complet
      }
    } catch (error: any) {
      console.error('Erreur scan natif:', error);
      stopScanning(); // Arrêt complet en cas d'erreur
    }
  };
  const startHtml5QrcodeScanning = async () => {
    try {
      setError(null);
      setIsScanning(true);

      const html5QrCode = new Html5Qrcode("video-preview-container");
      html5QrcodeRef.current = html5QrCode;

      // Configuration optimisée pour codes-barres
      const config = {
        fps: 20, // Plus élevé pour meilleure réactivité
        qrbox: function(viewfinderWidth: number, viewfinderHeight: number) {
          // Zone de scan adaptative
          const minEdgePercentage = 0.7;
          const minEdgeSize = Math.min(viewfinderWidth, viewfinderHeight);
          const qrboxSize = Math.floor(minEdgeSize * minEdgePercentage);
          return {
            width: Math.min(qrboxSize, 350),
            height: Math.min(qrboxSize * 0.6, 200) // Plus large pour codes-barres
          };
        },
        aspectRatio: 1.777778,
        disableFlip: false,
        videoConstraints: {
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 },
          facingMode: "environment"
        },
        supportedScanTypes: [1], // Focus sur BARCODE uniquement pour de meilleures performances
        experimentalFeatures: {
          useBarCodeDetectorIfSupported: true // Utilise l'API native si disponible
        }
      };

      const cameraId = selectedCamera || { facingMode: "environment" };

      let lastScannedCode = '';
      let lastScanTime = 0;

      await html5QrCode.start(
        cameraId,
        config,
        (decodedText, decodedResult) => {
          // Éviter les scans répétitifs
          const now = Date.now();
          if (decodedText === lastScannedCode && now - lastScanTime < 2000) {
            return;
          }
          
          lastScannedCode = decodedText;
          lastScanTime = now;
          
          // Succès du scan
          if (navigator.vibrate) {
            navigator.vibrate([100, 50, 100]);
          }
          onBarcodeScanned(decodedText);
          onScanSuccess?.(decodedText);
          if (!continuousMode) {
            stopScanning();
          }
        },
        (errorMessage) => {
          // Erreur silencieuse - continue le scan
        }
      );

      // Activer le flash après un délai
      setTimeout(() => {
        try {
          setTorchEnabled(true);
        } catch (e) {
          console.warn('Flash non supporté');
        }
      }, 1000);
      
    } catch (error: any) {
      console.error('Erreur html5-qrcode:', error);
      // Fallback automatique vers ZXing
      setScannerMethod('zxing');
      setTimeout(() => startZXingScanning(), 500);
    }
  };

  const captureAndRotateImage = async (video: HTMLVideoElement, rotation: number): Promise<HTMLImageElement> => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    
    // Dimensions originales
    const originalWidth = video.videoWidth;
    const originalHeight = video.videoHeight;
    
    // Ajuster les dimensions du canvas selon la rotation
    if (rotation === 90 || rotation === 270) {
      canvas.width = originalHeight;
      canvas.height = originalWidth;
    } else {
      canvas.width = originalWidth;
      canvas.height = originalHeight;
    }
    
    // Sauvegarder l'état du contexte
    ctx.save();
    
    // Déplacer l'origine au centre du canvas
    ctx.translate(canvas.width / 2, canvas.height / 2);
    
    // Appliquer la rotation
    ctx.rotate((rotation * Math.PI) / 180);
    
    // Dessiner l'image en tenant compte de la rotation
    ctx.drawImage(video, -originalWidth / 2, -originalHeight / 2, originalWidth, originalHeight);
    
    // Restaurer l'état du contexte
    ctx.restore();
    
    // Créer une image à partir du canvas
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.src = canvas.toDataURL('image/jpeg', 0.9);
    });
  };

  const startZXingScanning = async () => {
    try {
      setError(null);
      setIsScanning(true);
      const codeReader = new BrowserMultiFormatReader();
      codeReaderRef.current = codeReader;

      // Contraintes optimisées pour codes-barres
      const constraints: MediaStreamConstraints = {
        video: {
          deviceId: selectedCamera ? { exact: selectedCamera } : undefined,
          facingMode: selectedCamera ? undefined : { ideal: 'environment' },
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 },
          frameRate: { ideal: 30, min: 15 }
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const video = document.getElementById('video-preview') as HTMLVideoElement;
      if (video) {
        video.srcObject = stream;
        
        await new Promise<void>((resolve) => {
          video.onloadedmetadata = () => {
            video.play();
            resolve();
          };
        });

        // Activer le flash
        const track = stream.getVideoTracks()[0];
        if (track && 'torch' in track.getCapabilities()) {
          try {
            await track.applyConstraints({
              advanced: [{ torch: true } as any]
            });
            setTorchEnabled(true);
          } catch (e) {
            console.warn('Flash non supporté');
          }
        }

        let lastScannedCode = '';
        let lastScanTime = 0;
        let scanInterval: NodeJS.Timeout;
        
        const tryDecode = async (): Promise<string | null> => {
          try {
            // Essayer d'abord directement sur la vidéo (0°)
            try {
              const directResult = await codeReader.decodeOnceFromVideoElement(video);
              if (directResult) {
                secureLogger.info('BarcodeScanner', 'Code décodé à 0°', { hasResult: true });
                return directResult.getText();
              }
            } catch (e) {
              // Continue avec les rotations
            }
            
            // Essayer les différentes rotations
            const rotations = [90, 180, 270];
            
            for (const rotation of rotations) {
              try {
                secureLogger.debug('BarcodeScanner', `Essai rotation ${rotation}°`);
                const rotatedImg = await captureAndRotateImage(video, rotation);
                const result = await codeReader.decodeFromImageElement(rotatedImg);
                if (result) {
                  secureLogger.info('BarcodeScanner', `Code décodé à ${rotation}°`, { hasResult: true });
                  return result.getText();
                }
              } catch (e) {
                secureLogger.debug('BarcodeScanner', `Échec rotation ${rotation}°`);
                // Continue avec la rotation suivante
              }
            }
            
            return null;
            
          } catch (error) {
            console.error('Erreur tryDecode:', error);
            return null;
          }
        };

        const startContinuousScan = () => {
          scanInterval = setInterval(async () => {
            const result = await tryDecode();
            if (result) {
              const now = Date.now();
              // Éviter les scans répétitifs
              if (result === lastScannedCode && now - lastScanTime < 2000) {
                return;
              }
              
              lastScannedCode = result;
              lastScanTime = now;
              
              clearInterval(scanInterval);
              if (navigator.vibrate) {
                navigator.vibrate([100, 50, 100]);
              }
              secureLogger.info('BarcodeScanner', 'Code-barres scanné avec succès', { hasResult: true });
              onBarcodeScanned(result);
              onScanSuccess?.(result);
              if (!continuousMode) {
                stopScanning();
              }
            }
          }, 300); // Scan plus fréquent pour meilleure réactivité
        };

        startContinuousScan();

        codeReaderRef.current = {
          ...codeReader,
          stopScan: () => {
            clearInterval(scanInterval);
            stream.getTracks().forEach(track => track.stop());
          }
        } as any;
      }
    } catch (error: any) {
      console.error('Erreur ZXing:', error);
      setError('Impossible d\'accéder à la caméra. Vérifiez les permissions.');
      stopScanning();
    }
  };

  const startWebScanning = async () => {
    if (scannerMethod === 'html5qrcode') {
      await startHtml5QrcodeScanning();
    } else {
      await startZXingScanning();
    }
  };
  const startScanning = async () => {
    // Forcer l'utilisation du scanner natif sur les plateformes mobiles
    if (isNative || (isMobile && Capacitor.isNativePlatform())) {
      await startNativeScanning();
    } else {
      await startWebScanning();
    }
  };
  const toggleTorch = async () => {
    if (isNative) {
      setTorchEnabled(!torchEnabled);
    } else {
      try {
        if (scannerMethod === 'html5qrcode' && html5QrcodeRef.current) {
          // HTML5-QRCode gestion du flash
          const newTorchState = !torchEnabled;
          setTorchEnabled(newTorchState);
        } else {
          // ZXing gestion du flash
          const video = document.getElementById('video-preview') as HTMLVideoElement;
          if (video && video.srcObject) {
            const stream = video.srcObject as MediaStream;
            const track = stream.getVideoTracks()[0];
            if (track && 'torch' in track.getCapabilities()) {
              await track.applyConstraints({
                advanced: [{ torch: !torchEnabled } as any]
              });
              setTorchEnabled(!torchEnabled);
            }
          }
        }
      } catch (e) {
        console.warn('Erreur toggle flash:', e);
      }
    }
  };

  const stopScanning = () => {
    setIsScanning(false);
    setError(null);
    setManualBarcode('');
    
    if (isNative) {
      CapacitorBarcodeScanner.stopScan();
      document.body.classList.remove('scanner-active');
    } else {
      try {
        // Arrêter html5-qrcode
        if (html5QrcodeRef.current) {
          html5QrcodeRef.current.stop().catch(console.warn);
          html5QrcodeRef.current = null;
        }
        
        // Arrêter ZXing et ses intervalles
        if (codeReaderRef.current && (codeReaderRef.current as any).stopScan) {
          (codeReaderRef.current as any).stopScan();
        }
        codeReaderRef.current = null;
        
        // Nettoyer complètement la vidéo et ses flux
        const video = document.getElementById('video-preview') as HTMLVideoElement | null;
        if (video) {
          const stream = (video as any).srcObject as MediaStream | null;
          if (stream) {
            stream.getTracks().forEach((track) => {
              track.stop();
              secureLogger.debug('BarcodeScanner', 'Video track stopped', { trackKind: track.kind });
            });
          }
          (video as any).srcObject = null;
          video.removeAttribute('src');
          video.load?.();
        }
        
        // Nettoyer le conteneur html5-qrcode
        const container = document.getElementById('video-preview-container');
        if (container) {
          container.innerHTML = '';
        }
        
        // Nettoyer tous les flux médias actifs
        navigator.mediaDevices?.getUserMedia({ video: false, audio: false }).catch(() => {});
        
      } catch (e) {
        console.warn('Failed to stop camera stream', e);
      }
    }
    setTorchEnabled(false);
  };

  // Cleanup au démontage du composant
  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, []);
  const defaultTrigger = (
    <Button 
      variant="outline" 
      size={isMobile ? "sm" : "icon"} 
      onClick={() => {
        setIsOpen(true);
        if (autoStart) {
          // Démarrer automatiquement après ouverture si demandé explicitement
          setTimeout(() => {
            startScanning();
          }, 200);
        }
      }}
    >
      <Camera className="h-4 w-4" />
      {isMobile && <span className="ml-2">Scanner</span>}
    </Button>
  );
  return (
    <Dialog open={isOpen} onOpenChange={open => {
      setIsOpen(open);
      if (!open) {
        stopScanning();
        setError(null);
        setManualBarcode('');
      } else if (autoStart) {
        // Démarrer automatiquement si explicitement demandé
        setTimeout(() => {
          startScanning();
        }, 200);
      }
    }}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className={isMobile ? 'w-[95vw] h-[95vh] max-w-none' : 'sm:max-w-lg'}>
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Scan className="h-5 w-5" />
              Scanner code-barres
              {isNative && <Smartphone className="h-4 w-4 text-primary" />}
            </div>
            
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {error && <Alert>
              <AlertDescription>{error}</AlertDescription>
            </Alert>}
          
          {/* Zone de scan */}
          {!isNative && isScanning ? (
            <div className="relative">
              {scannerMethod === 'html5qrcode' ? (
                <div id="video-preview-container" className={`w-full bg-black rounded-lg ${isMobile ? 'h-[45vh] min-h-[350px]' : 'h-[60vh] min-h-[450px]'}`} />
              ) : (
                <video id="video-preview" className={`w-full bg-black rounded-lg ${isMobile ? 'h-[45vh] min-h-[350px]' : 'h-[60vh] min-h-[450px]'}`} playsInline muted />
              )}
              
              {/* Overlay de guidage visuel */}
              <div className="absolute inset-0 flex items-center justify-center rounded-lg">
                {/* Bulle du panier en haut à droite dans la vidéo */}
                {showCartBubble && cartItemCount > 0 && (
                  <div className="absolute top-4 right-4 z-10">
                    <div className="bg-primary text-primary-foreground px-4 py-2 rounded-full shadow-lg border-2 border-primary/20">
                      <span className="text-sm font-bold">
                        {new Intl.NumberFormat('fr-FR', {
                          style: 'currency',
                          currency: 'EUR'
                        }).format(cartTotal)} • {cartItemCount} article{cartItemCount > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Zone de scan centrale */}
                <div className="relative w-64 h-40 border-2 border-green-400 rounded-lg bg-transparent">
                  {/* Coins de guidage */}
                  <div className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-green-400 rounded-tl-lg"></div>
                  <div className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-green-400 rounded-tr-lg"></div>
                  <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-green-400 rounded-bl-lg"></div>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-green-400 rounded-br-lg"></div>
                  
                  {/* Ligne de scan animée */}
                  <div className="absolute inset-x-0 top-1/2 h-0.5 bg-green-400 opacity-75 animate-pulse"></div>
                </div>
                
                {/* Instructions en bas */}
                <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 text-white text-center backdrop-blur-sm bg-black/50 px-4 py-2 rounded-lg">
                  <p className="text-sm font-medium">Positionnez le code-barres dans le cadre</p>
                  <p className="text-xs opacity-90">Maintenir stable pour un scan optimal</p>
                </div>
              </div>
              
              {/* Contrôles */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                <Button size="sm" variant="secondary" onClick={toggleTorch}>
                  <Flashlight className={`h-4 w-4 ${torchEnabled ? 'text-yellow-400' : ''}`} />
                </Button>
                <Button size="sm" variant="destructive" onClick={stopScanning}>
                  Arrêter
                </Button>
              </div>
            </div>
          ) : (
            !autoStart && (
              <div className="text-center space-y-4">
                <div className="p-6 border-2 border-dashed border-primary/25 rounded-lg bg-gradient-to-br from-primary/5 to-primary/10">
                  <Camera className="h-12 w-12 mx-auto mb-4 text-primary" />
                  <h3 className="font-medium mb-2">
                    Scanner code-barres {isNative ? '(Natif)' : `(${scannerMethod === 'html5qrcode' ? 'HTML5-QRCode' : 'ZXing'})`}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {isNative ? 'Scanner natif multi-orientation avec flash' : 
                     scannerMethod === 'html5qrcode' ? 'Scanner optimisé 0°/90°/180°/270° avec détection automatique' : 
                     'Scanner multi-rotation 0°/90°/180°/270° haute résolution'}
                  </p>
                
                  <div className="space-y-3">
                    <Button onClick={startScanning} className="w-full" disabled={isScanning} size="lg">
                      {isScanning ? (
                        <>
                          <Scan className="mr-2 h-4 w-4 animate-pulse" />
                          Scan en cours...
                        </>
                      ) : (
                        <>
                          <Camera className="mr-2 h-4 w-4" />
                          Démarrer scanner
                        </>
                      )}
                    </Button>
                    
                    {/* Sélection de caméra et méthode pour le web */}
                    {!isNative && (
                      <div className="space-y-2 text-xs text-center text-muted-foreground">
                        {cameraDevices.length > 1 && (
                          <select 
                            value={selectedCamera} 
                            onChange={(e) => setSelectedCamera(e.target.value)}
                            className="bg-background border border-border rounded px-2 py-1 text-xs w-full"
                          >
                            <option value="">Caméra par défaut</option>
                            {cameraDevices.map(device => (
                              <option key={device.deviceId} value={device.deviceId}>
                                {device.label || `Caméra ${device.deviceId.substring(0, 5)}`}
                              </option>
                            ))}
                          </select>
                        )}
                        <select 
                          value={scannerMethod} 
                          onChange={(e) => setScannerMethod(e.target.value as 'html5qrcode' | 'zxing')}
                          className="bg-background border border-border rounded px-2 py-1 text-xs w-full"
                        >
                          <option value="html5qrcode">HTML5-QRCode (Recommandé)</option>
                          <option value="zxing">ZXing Browser</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          )}

          {/* Saisie manuelle */}
          <form onSubmit={handleManualSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="manual-barcode">Ou saisir manuellement</Label>
              <Input id="manual-barcode" value={manualBarcode} onChange={e => setManualBarcode(e.target.value)} placeholder="Entrez le code-barres" disabled={isScanning} className="text-base" />
            </div>
            <Button type="submit" variant="outline" className="w-full" disabled={isScanning || !manualBarcode.trim()}>
              Utiliser ce code
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};